import { useState, useEffect, useCallback } from 'react';
import { Play, Pause, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { formatTime } from '../lib/utils';

export const RestTimer = ({ initialTime = 90, onComplete }) => {
  const [time, setTime] = useState(initialTime);
  const [isRunning, setIsRunning] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    setTime(initialTime);
    setIsRunning(false);
    setIsComplete(false);
  }, [initialTime]);

  useEffect(() => {
    let interval = null;
    
    if (isRunning && time > 0) {
      interval = setInterval(() => {
        setTime((t) => {
          if (t <= 1) {
            setIsRunning(false);
            setIsComplete(true);
            onComplete?.();
            return 0;
          }
          return t - 1;
        });
      }, 1000);
    }
    
    return () => clearInterval(interval);
  }, [isRunning, time, onComplete]);

  const toggle = useCallback(() => {
    if (time === 0) {
      setTime(initialTime);
      setIsComplete(false);
    }
    setIsRunning((r) => !r);
  }, [time, initialTime]);

  const reset = useCallback(() => {
    setTime(initialTime);
    setIsRunning(false);
    setIsComplete(false);
  }, [initialTime]);

  const progress = (time / initialTime) * 100;

  return (
    <div 
      className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
        isComplete 
          ? 'bg-[#00E676]/20 border border-[#00E676]/30' 
          : 'bg-[#161625] border border-white/5'
      }`}
      data-testid="rest-timer"
    >
      {/* Progress bar */}
      <div className="flex-1 h-2 bg-[#2A2A40] rounded-full overflow-hidden">
        <div 
          className={`h-full transition-all duration-1000 ${
            isComplete ? 'bg-[#00E676]' : time <= 10 ? 'bg-red-500' : 'bg-[#00E676]'
          }`}
          style={{ width: `${progress}%` }}
        />
      </div>
      
      {/* Time display */}
      <span 
        className={`font-mono text-lg font-bold min-w-[50px] text-center ${
          isComplete ? 'text-[#00E676]' : time <= 10 ? 'text-red-500' : 'text-white'
        }`}
        data-testid="timer-display"
      >
        {formatTime(time)}
      </span>
      
      {/* Controls */}
      <div className="flex gap-1">
        <Button
          size="icon"
          variant="ghost"
          onClick={toggle}
          className="h-10 w-10 hover:bg-white/10"
          data-testid="timer-toggle"
        >
          {isRunning ? <Pause size={18} /> : <Play size={18} />}
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={reset}
          className="h-10 w-10 hover:bg-white/10"
          data-testid="timer-reset"
        >
          <RotateCcw size={18} />
        </Button>
      </div>
    </div>
  );
};
